import MapboxViewer from "@/components/mapbox/MapboxViewer";
import { autobind } from "core-decorators";
import { MapboxCustomObject } from "./MapboxCustomObject";
import type { FeaturePDFOverlayData } from "@/types/content";
import { makePdfOverlayFileUrl } from "@/utils/url-utils";
import {
  calculateRotatedCoordinates,
  convertPdfPageToImage,
} from "@/utils/pdf-utils";
import { MAPBOX_MIN_ZOOM } from "@/constant";

export class MapboxTileOverlayObject extends MapboxCustomObject {
  private _sourceId: string;
  private _layerId: string;
  private width: number = 0;
  private height: number = 0;

  constructor(mapboxViewer: MapboxViewer, content: FeaturePDFOverlayData) {
    super(mapboxViewer, content);
    this._contentId = content.id;
    this._sourceId = `tile-overlay-source-${content.id}`;
    this._layerId = `tile-overlay-layer-${content.id}`;
    this.init(content);
  }

  @autobind
  init(content: FeaturePDFOverlayData) {
    this._content = content;
    this._contentId = content.id;
    this.addTileLayer();
  }

  private addTileLayer = async () => {
    const content = this._content as FeaturePDFOverlayData;

    // Use the URL from content properties
    // const url = `${makePdfOverlayUrl(content.id)}/{z}/{x}/{y}.png`;
    // const url =
    //   "http://127.0.0.1:8080/tmp/20250913_091329/tiles/{z}/{x}/{y}.png";

    const pdfUrl = makePdfOverlayFileUrl(content.id);

    const response = await fetch(pdfUrl);
    const blob = await response.blob();
    const file = new File([blob], "pdf.pdf", { type: "application/pdf" });
    // const arrayBuffer = await file.arrayBuffer();
    // const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const pdfImageData = await convertPdfPageToImage(file, 1, 1.5);
    this.width = pdfImageData.width;
    this.height = pdfImageData.height;
    const newCoordinates = calculateRotatedCoordinates(
      content.properties.scale,
      pdfImageData.width,
      pdfImageData.height,
      content.properties.position as [number, number],
      content.properties.rotation
    );
    this._mapboxViewer._mapViewer.addSource(this._sourceId, {
      type: "image",
      url: pdfImageData.imageUrl,
      coordinates: newCoordinates as [
        [number, number],
        [number, number],
        [number, number],
        [number, number]
      ],
    });

    // Add raster layer for the image
    this._mapboxViewer._mapViewer.addLayer({
      id: this._layerId,
      type: "raster",
      source: this._sourceId,
      paint: {
        "raster-opacity":
          content.config.opacity === undefined ? 1 : content.config.opacity,
      },
      layout: {
        visibility: content.config.visible ? "visible" : "none",
      },
      minzoom: MAPBOX_MIN_ZOOM - 2,
    });

    // Add the tile source
    // this._mapboxViewer._mapViewer.addSource(this._sourceId, {
    //   type: "raster",
    //   tiles: [url],
    //   scheme: "tms",

    //   // tileSize: 256,
    //   // minzoom: content.properties.minZoom || 12,
    //   // maxzoom: content.properties.maxZoom || 22,
    //   //   bounds: content.properties.bounds as [number, number, number, number],
    // });

    // // Add the raster layer
    // this._mapboxViewer._mapViewer.addLayer({
    //   id: this._layerId,
    //   type: "raster",
    //   source: this._sourceId,
    //   //   paint: {
    //   //     "raster-opacity": content.config.opacity || 1.0,
    //   //     "raster-resampling": "linear",
    //   //   },
    //   //   layout: {
    //   //     visibility: content.config.visible ? "visible" : "none",
    //   //   },
    // });
    this._mapboxViewer.entities.tileOverlayEntity?.add(this);
  };

  @autobind
  updateContent(content: FeaturePDFOverlayData) {
    this._content = content;
    this.updateLayer();
  }

  @autobind
  updateLayer() {
    const content = this._content as FeaturePDFOverlayData;
    const layer = this._mapboxViewer._mapViewer.getLayer(this._layerId);

    if (layer) {
      this._mapboxViewer._mapViewer.setPaintProperty(
        this._layerId,
        "raster-opacity",
        content.config.opacity === undefined ? 1 : content.config.opacity
      );

      this._mapboxViewer._mapViewer.setLayoutProperty(
        this._layerId,
        "visibility",
        content.config.visible ? "visible" : "none"
      );

      const newCoordinates = calculateRotatedCoordinates(
        content.properties.scale,
        this.width,
        this.height,
        content.properties.position as [number, number],
        content.properties.rotation
      );
      const source = this._mapboxViewer._mapViewer.getSource(
        this._sourceId
      ) as mapboxgl.ImageSource;
      if (source) {
        source.setCoordinates(
          newCoordinates as [
            [number, number],
            [number, number],
            [number, number],
            [number, number]
          ]
        );
      }
    }
  }

  @autobind
  setOpacity(opacity: number) {
    const content = this._content as FeaturePDFOverlayData;
    content.config.opacity = Math.max(0.1, Math.min(1, opacity)); // Clamp between 0 and 1
    this.updateLayer();
  }

  @autobind
  setVisible(visible: boolean) {
    const content = this._content as FeaturePDFOverlayData;
    content.config.visible = visible;
    this.updateLayer();
  }

  @autobind
  dispose() {
    // Remove the layer and source
    if (this._mapboxViewer._mapViewer.getLayer(this._layerId)) {
      this._mapboxViewer._mapViewer.removeLayer(this._layerId);
    }

    if (this._mapboxViewer._mapViewer.getSource(this._sourceId)) {
      this._mapboxViewer._mapViewer.removeSource(this._sourceId);
    }
  }

  get sourceId(): string {
    return this._sourceId;
  }

  get layerId(): string {
    return this._layerId;
  }

  get visible(): boolean {
    return (this._content as FeaturePDFOverlayData).config.visible;
  }

  set visible(value: boolean) {
    this.setVisible(value);
  }

  get opacity(): number {
    return (this._content as FeaturePDFOverlayData).config.opacity === undefined
      ? 1
      : (this._content as FeaturePDFOverlayData).config.opacity;
  }

  set opacity(value: number) {
    this.setOpacity(value);
  }

  focusOn() {
    const position = this._content.properties.position as [number, number];
    this._mapboxViewer._mapViewer.flyTo({
      center: position as mapboxgl.LngLatLike,
      zoom: 18,
    });
  }
}
